import React, { useEffect, useState } from 'react';
import { FieldProps } from '@rjsf/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

interface ArgoNamespace {
  cluster: string;
  namespace: string;
}

/**
 * ArgoCDNamespacePicker
 * 
 * 自定义字段：从 ArgoCD Project 动态加载可用的 Namespace 列表
 * 支持根据选择的 cluster 过滤 namespace
 * 
 * 使用方式：
 * ```yaml
 * namespace:
 *   title: 命名空间
 *   type: string
 *   ui:field: ArgoCDNamespacePicker
 *   ui:options:
 *     projectField: systemName    # 关联的 ArgoCD Project 字段名
 *     clusterField: cluster       # 关联的 cluster 字段名（可选）
 * ```
 */
export const ArgoCDNamespacePicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  uiSchema,
  idSchema,
  schema,
}: FieldProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  
  const [namespaces, setNamespaces] = useState<ArgoNamespace[]>([]);
  const [filteredNamespaces, setFilteredNamespaces] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [useCustomInput, setUseCustomInput] = useState(false);

  // 获取关联字段的值
  const projectField = uiSchema?.['ui:options']?.projectField as string;
  const clusterField = uiSchema?.['ui:options']?.clusterField as string;
  const projectName = projectField ? formContext?.formData?.[projectField] : undefined;
  const selectedCluster = clusterField ? formContext?.formData?.[clusterField] : undefined;

  useEffect(() => {
    const loadNamespaces = async () => {
      if (!projectName) {
        setNamespaces([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(undefined);

        const backendUrl = await discoveryApi.getBaseUrl('argocd');
        const { token } = await identityApi.getCredentials();

        // 从 ArgoCD 后端获取 Project 的 destinations
        const response = await fetch(
          `${backendUrl}/projects/${encodeURIComponent(projectName)}`,
          {
            headers: {
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            setError(`Project '${projectName}' not found`);
            setNamespaces([]);
          } else {
            throw new Error(`Failed to load namespaces: ${response.statusText}`);
          }
          return;
        }

        const data = await response.json();
        const destinations = (data.spec?.destinations || []).map((dest: any) => ({
          cluster: dest.name || dest.server,
          namespace: dest.namespace,
        }));

        setNamespaces(destinations);
      } catch (err) {
        console.error('Failed to load ArgoCD namespaces:', err);
        setError(err instanceof Error ? err.message : 'Failed to load namespaces');
      } finally {
        setLoading(false);
      }
    };

    loadNamespaces();
  }, [discoveryApi, identityApi, projectName]);

  // 根据选择的 cluster 过滤 namespace
  useEffect(() => {
    if (selectedCluster) {
      const filtered = namespaces
        .filter(ns => ns.cluster === selectedCluster || ns.cluster === '*')
        .map(ns => ns.namespace);
      setFilteredNamespaces([...new Set(filtered)]); // 去重
    } else {
      const all = namespaces.map(ns => ns.namespace);
      setFilteredNamespaces([...new Set(all)]);
    }
  }, [namespaces, selectedCluster]);

  // 如果没有可选的 namespace，允许手动输入
  const showCustomInput = useCustomInput || (filteredNamespaces.length === 0 && !loading);

  if (showCustomInput) {
    return (
      <FormControl
        margin="normal"
        required={required}
        error={rawErrors?.length > 0 && !formData}
        fullWidth
      >
        <TextField
          id={idSchema.$id}
          label={schema.title || 'Namespace'}
          value={formData || ''}
          onChange={e => onChange(e.target.value)}
          helperText={
            <span>
              手动输入命名空间名称
              {filteredNamespaces.length > 0 && (
                <span 
                  style={{ marginLeft: 8, cursor: 'pointer', color: '#1976d2' }}
                  onClick={() => setUseCustomInput(false)}
                >
                  切换到下拉选择
                </span>
              )}
            </span>
          }
          required={required}
          error={rawErrors?.length > 0 && !formData}
        />
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema.$id}>
        {schema.title || 'Namespace'}
      </InputLabel>
      <Select
        id={idSchema.$id}
        value={formData || ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading || !projectName}
      >
        {!projectName && (
          <MenuItem value="" disabled>
            请先选择 System
          </MenuItem>
        )}
        {loading && (
          <MenuItem value="" disabled>
            加载中...
          </MenuItem>
        )}
        {error && (
          <MenuItem value="" disabled>
            {error}
          </MenuItem>
        )}
        {!loading && !error && filteredNamespaces.length === 0 && projectName && (
          <MenuItem value="" disabled>
            无可用命名空间
          </MenuItem>
        )}
        {filteredNamespaces.map(ns => (
          <MenuItem key={ns} value={ns}>
            {ns}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {selectedCluster ? `已选集群: ${selectedCluster}` : '选择命名空间'}
        <span 
          style={{ marginLeft: 8, cursor: 'pointer', color: '#1976d2' }}
          onClick={() => setUseCustomInput(true)}
        >
          手动输入
        </span>
      </FormHelperText>
      {rawErrors?.length > 0 && !formData && (
        <FormHelperText error>此字段为必填项</FormHelperText>
      )}
    </FormControl>
  );
};

